import { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { StoreProvider } from "@/context/StoreContext";
import StorefrontLayout from "@/components/storefront/StorefrontLayout";
import Home from "@/pages/storefront/Home";
import CatalogPage from "@/pages/storefront/CatalogPage";
import DadyaParfumPage from "@/pages/storefront/DadyaParfumPage";
import ProductPage from "@/pages/storefront/ProductPage";
import CartPage from "@/pages/storefront/CartPage";
import CheckoutPage from "@/pages/storefront/CheckoutPage";
import OrderSuccessPage from "@/pages/storefront/OrderSuccessPage";
import PaymentResultPage from "@/pages/storefront/PaymentResultPage";
import MockThreeDSPage from "@/pages/storefront/MockThreeDSPage";
import OrderTrackingPage from "@/pages/storefront/OrderTrackingPage";
import { AuthCallback, ForgotPasswordPage, LoginPage, RegisterPage, ResetPasswordPage } from "@/pages/storefront/AuthPages";
import AccountPage from "@/pages/storefront/AccountPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import ProductsPage from "@/pages/admin/ProductsPage";
import ProductFormPage from "@/pages/admin/ProductFormPage";
import OrdersPage from "@/pages/admin/OrdersPage";
import ReturnsPage from "@/pages/admin/ReturnsPage";
import QuotesPage from "@/pages/admin/QuotesPage";
import SuppliersPage from "@/pages/admin/SuppliersPage";
import ReportsPage from "@/pages/admin/ReportsPage";
import EmailsPage from "@/pages/admin/EmailsPage";
import CustomerDetailPage from "@/pages/admin/CustomerDetailPage";
import HomepagePage from "@/pages/admin/HomepagePage";
import MediaPage from "@/pages/admin/MediaPage";
import CategoriesPage from "@/pages/admin/CategoriesPage";
import CustomersPage from "@/pages/admin/CustomersPage";
import MarketingPage from "@/pages/admin/MarketingPage";
import BannersPage from "@/pages/admin/BannersPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import ImportPage from "@/pages/admin/ImportPage";
import MarketplacePage from "@/pages/admin/MarketplacePage";
import InvoicesPage from "@/pages/admin/InvoicesPage";
import PaymentMethodsPage from "@/pages/admin/PaymentMethodsPage";
import PaymentLogsPage from "@/pages/admin/PaymentLogsPage";
import IntegrationLogsPage from "@/pages/admin/IntegrationLogsPage";
import PaytrPayPage from "@/pages/storefront/PaytrPayPage";

function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
}

function AppRoutes() {
  const location = useLocation();
  useLenis();

  if (location.hash?.includes("session_id=")) {
    return (
      <StorefrontLayoutWrapper>
        <AuthCallback />
      </StorefrontLayoutWrapper>
    );
  }

  return (
    <Routes>
      <Route element={<StorefrontLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/kategori/:categorySlug" element={<CatalogPage />} />
        <Route path="/dadya-parfum" element={<DadyaParfumPage />} />
        <Route path="/dadya-parfum/:categorySlug" element={<CatalogPage />} />
        <Route path="/marka/:brandSlug" element={<CatalogPage />} />
        <Route path="/ara" element={<CatalogPage />} />
        <Route path="/urun/:slug" element={<ProductPage />} />
        <Route path="/sepet" element={<CartPage />} />
        <Route path="/odeme" element={<CheckoutPage />} />
        <Route path="/siparis-basarili/:orderNumber" element={<OrderSuccessPage />} />
        <Route path="/odeme/sonuc" element={<PaymentResultPage />} />
        <Route path="/odeme/3d-guvenli" element={<MockThreeDSPage />} />
        <Route path="/odeme/paytr" element={<PaytrPayPage />} />
        <Route path="/siparis-takip" element={<OrderTrackingPage />} />
        <Route path="/giris" element={<LoginPage />} />
        <Route path="/kayit" element={<RegisterPage />} />
        <Route path="/sifremi-unuttum" element={<ForgotPasswordPage />} />
        <Route path="/sifre-sifirla" element={<ResetPasswordPage />} />
        <Route path="/hesabim" element={<AccountPage />} />
      </Route>
      <Route path="/admin/giris" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="urunler" element={<ProductsPage />} />
        <Route path="urunler/yeni" element={<ProductFormPage />} />
        <Route path="urunler/:productId" element={<ProductFormPage />} />
        <Route path="kategoriler" element={<CategoriesPage />} />
        <Route path="siparisler" element={<OrdersPage />} />
        <Route path="iadeler" element={<ReturnsPage />} />
        <Route path="teklifler" element={<QuotesPage />} />
        <Route path="tedarikciler" element={<SuppliersPage />} />
        <Route path="faturalar" element={<InvoicesPage />} />
        <Route path="musteriler" element={<CustomersPage />} />
        <Route path="musteriler/:customerId" element={<CustomerDetailPage />} />
        <Route path="raporlar" element={<ReportsPage />} />
        <Route path="epostalar" element={<EmailsPage />} />
        <Route path="pazarlama" element={<MarketingPage />} />
        <Route path="marketplace" element={<MarketplacePage />} />
        <Route path="odeme-yontemleri" element={<PaymentMethodsPage />} />
        <Route path="odeme-loglari" element={<PaymentLogsPage />} />
        <Route path="entegrasyon-loglari" element={<IntegrationLogsPage />} />
        <Route path="anasayfa" element={<HomepagePage />} />
        <Route path="medya" element={<MediaPage />} />
        <Route path="bannerlar" element={<BannersPage />} />
        <Route path="aktarim" element={<ImportPage />} />
        <Route path="ayarlar" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<StorefrontLayoutWrapper><NotFound /></StorefrontLayoutWrapper>} />
    </Routes>
  );
}

function StorefrontLayoutWrapper({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#FAF9F5]">
      <main className="flex-1">{children}</main>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
      <p className="font-display font-black text-6xl text-[#121214]">404</p>
      <a href="/" className="text-amber-700 font-semibold underline" data-testid="not-found-home">Anasayfaya dön</a>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <StoreProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-center" richColors toastOptions={{ style: { borderRadius: "14px" } }} />
        </BrowserRouter>
      </StoreProvider>
    </HelmetProvider>
  );
}

export default App;
